import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { CourseControl } from "../CourseControl/CourseControl";
import { CourseAndDurationControlQuery } from "./__generated__/CourseAndDurationControlQuery.graphql";
import { BucketDurationControl } from "./components/BucketDurationControl";
import { DurationControlSkeleton } from "./components/DurationControlSkeleton";
import { CourseDurationOption } from "./types";

/**
 * This control handles querying for course and duration options.
 * It will internally decide if the user is allowed to select courses or not.
 * It will fallback to using duration even for no course selection scenarios.
 */
export const CourseAndDurationControl = ({
  slotId,
  agendaId,
  startTime,
  memberId,
}: {
  slotId: string;
  agendaId: string;
  startTime: string;
  memberId: string;
}) => {
  const [queryRef, loadQuery] = useQueryLoader<CourseAndDurationControlQuery>(
    CourseAndDurationControlQueryString
  );

  useEffect(() => {
    loadQuery(
      {
        slotId,
        agendaId,
        startTime,
        bookingType: "STANDARD",
        memberId,
      },
      { fetchPolicy: "network-only" }
    );
  }, [slotId, agendaId, startTime, loadQuery, memberId]);

  return (
    <Suspense fallback={<DurationControlSkeleton />}>
      {queryRef ? <CourseAndDurationControlInner queryRef={queryRef} /> : null}
    </Suspense>
  );
};

export const CourseAndDurationControlInner = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<CourseAndDurationControlQuery>;
}) => {
  const data = usePreloadedQuery(CourseAndDurationControlQueryString, queryRef);
  const durations =
    data?.node?.agenda?.facility.durations?.flatMap((d) =>
      d !== null ? [d] : []
    ) || [];

  const endSlotOptions =
    data?.node?.endSlotOptions?.edges?.flatMap((e) =>
      e?.node && e.node.availability === "AVAILABLE" ? e.node.time : []
    ) || [];

  const freeSlotsLength = endSlotOptions.length;

  const options = durations.flatMap((d) => {
    const requiredSlots = (d ?? 0) / 15;
    if (requiredSlots > freeSlotsLength) {
      return [];
    }
    return [d];
  });

  const maxDuration = options[options.length - 1];

  const currentCourseDurationOption: CourseDurationOption = {
    maxDurationMins: maxDuration ?? 0,
    startSlot: {
      id: data.node?.id ?? "",
      time: data.node?.time ?? "",
      course: {
        id: data.node?.course?.id ?? "",
        name: data.node?.course?.name ?? "",
        description: data.node?.course?.description ?? "",
        courseBucket: {
          id: data.node?.course?.courseBucket?.id ?? "",
        },
      },
    },
  };

  return (
    <>
      <BucketDurationControl
        nodeRef={data.node ?? null}
        currentCourseDurationOption={currentCourseDurationOption}
      />
      <CourseControl nodeRef={data.node ?? null} />
    </>
  );
};

export const CourseAndDurationControlQueryString = graphql`
  query CourseAndDurationControlQuery(
    $slotId: ID!
    $agendaId: ID!
    $startTime: Time!
    $bookingType: BookingType!
    $memberId: ID!
    $bookingId: ID
  ) {
    node(id: $slotId) {
      ...BucketDurationControlNode
      ...CourseControlNode
      ... on TimeSlot {
        ...useValidDurationsNode
        id
        time
        agenda {
          facility {
            durations
          }
        }
        course {
          id
          courseBucket {
            id
          }
          name
          description
        }
        endSlotOptions(
          bookingId: $bookingId
          bookingType: $bookingType
          memberId: $memberId
        ) {
          edges {
            node {
              time
              availability
            }
          }
        }
      }
    }
  }
`;
